/**
 * The single HTTP entry point for the client.
 *
 * Three responsibilities, none of which belong in a component:
 *   1. Same-origin requests. In development the Vite proxy makes `/v1/...`
 *      same-origin; in production the app is served from the same origin as
 *      the API. `credentials: 'include'` is set anyway so a future
 *      split-origin deployment does not silently drop the session cookie.
 *   2. problem+json decoding into a typed error the UI can branch on.
 *   3. Money decoding. Every money value crossing this boundary becomes a
 *      `Money` (bigint-backed) via the shared codec. Nothing downstream of
 *      here is permitted to do arithmetic on `amountCents`.
 */
import { fromWire, type Money, type Problem } from '@kickback/contracts';

export class ApiError extends Error {
  constructor(
    readonly problem: Problem,
    readonly status: number,
  ) {
    super(problem.title);
    this.name = 'ApiError';
  }

  get requestId(): string | undefined {
    return this.problem.requestId;
  }
}

let requestCounter = 0;

function nextRequestId(): string {
  requestCounter += 1;
  return `web-${Date.now().toString(36)}-${requestCounter.toString(36)}`;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /**
   * Sent as the `Idempotency-Key` header (§4.1.5).
   *
   * Usually unnecessary: a body carrying its own `idempotencyKey` supplies this
   * automatically, see `idempotencyKeyFor`. Pass it explicitly only for a
   * request whose key is not in the body.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

/**
 * The key to send as a header, preferring an explicit option and otherwise
 * taking the one already in the body.
 *
 * The fallback is the point. This option existed from the start and had no
 * callers whatsoever: every money-moving request put its key in the body, where
 * the per-endpoint unique index wants it, and nobody also passed it here — so
 * the header was never sent and the server's HTTP idempotency layer never saw a
 * single request. Requiring the same value in two places is a rule that gets
 * followed until the day someone adds an endpoint and forgets, and there is no
 * type error when they do.
 *
 * Deriving it means a request that is idempotent at the ledger cannot silently
 * fail to be idempotent over HTTP.
 */
function idempotencyKeyFor(options: RequestOptions): string | undefined {
  if (options.idempotencyKey !== undefined) return options.idempotencyKey;

  const body: unknown = options.body;
  if (typeof body !== 'object' || body === null) return undefined;

  const key: unknown = (body as { idempotencyKey?: unknown }).idempotencyKey;
  return typeof key === 'string' && key.length > 0 ? key : undefined;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const idempotencyKey = idempotencyKeyFor(options);

  const headers: Record<string, string> = {
    accept: 'application/json, application/problem+json',
    'x-request-id': nextRequestId(),
  };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (idempotencyKey !== undefined) headers['idempotency-key'] = idempotencyKey;

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  const payload: unknown = contentType.includes('json') ? await response.json() : await response.text();

  if (!response.ok) {
    const problem: Problem =
      typeof payload === 'object' && payload !== null && 'status' in payload
        ? (payload as Problem)
        : {
            type: 'about:blank',
            title: response.statusText || 'Request failed',
            status: response.status,
          };
    throw new ApiError(problem, response.status);
  }

  return payload as T;
}

/**
 * Decode a `{ amountCents, currency }` envelope from a response into `Money`.
 * Exported separately rather than applied automatically: a blanket deep-walk
 * over responses guessing which objects are money is exactly the kind of magic
 * that eventually mis-parses something.
 */
export function decodeMoney(value: { amountCents: number; currency: 'USD' }): Money {
  return fromWire(value);
}

/** True when the user simply is not signed in, as opposed to a real failure. */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/* ------------------------------------------------------------------ *
 * Wire shapes, mirroring the server's response schemas. Money fields
 * stay in wire form here and are decoded explicitly at the point of use.
 * ------------------------------------------------------------------ */

export interface MoneyWire {
  amountCents: number;
  currency: 'USD';
}

export interface PrincipalWire {
  id: string;
  kind: 'member' | 'creator' | 'merchant' | 'admin';
  displayName: string;
}

export interface SessionWire {
  email: string;
  principal: PrincipalWire;
  availablePrincipals: PrincipalWire[];
}

export interface BalancesWire {
  pending: MoneyWire;
  available: MoneyWire;
  inFlight: MoneyWire;
  lifetimeEarned: MoneyWire;
}

export interface EarningsWire extends BalancesWire {
  payoutBlockedReason: 'tax_information_required' | 'negative_balance' | null;
}

export interface ActivityWire {
  data: {
    id: string;
    occurredAt: string;
    eventType: string;
    accountType: string;
    amount: MoneyWire;
    /** Null where no single purchase explains the movement. */
    conversionId: string | null;
  }[];
  nextCursor: string | null;
}

export interface CardWire {
  id: string;
  scheme: string;
  lastFour: string;
  expiryMonth: number;
  expiryYear: number;
  status: string;
  enrolledAt: string;
}

export interface SplitTermsWire {
  platformBps: number;
  creatorBps: number;
  memberBps: number;
}

export interface ReferralCodeWire {
  id: string;
  code: string;
  terms: SplitTermsWire;
  active: boolean;
  createdAt: string;
}

export interface ConnectionWire {
  id: string;
  creatorId: string;
  creatorHandle: string | null;
  memberId: string;
  memberDisplayName: string | null;
  terms: SplitTermsWire;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: string;
}

/**
 * What enrolment returns now. Not a card — a reference to hand to the
 * provider's SDK. The card appears only once the provider confirms it, over a
 * webhook the browser is not part of, which is why nothing here returns one.
 */
export interface EnrolmentIntentWire {
  verificationRef: string;
  expiresAt: string;
}

/**
 * Re-exported from the package the API validates against, not restated here.
 *
 * The hand-written copy that used to live at this spot was already one field
 * behind — it had no `logoUrl` — which is how the mobile client's three
 * interfaces went wrong before it. Nothing below this line should grow a
 * second definition of a response shape.
 */
export type { OfferWire } from '@kickback/contracts/wire';

export interface PayoutEligibilityWire {
  available: MoneyWire;
  payable: MoneyWire;
  onHold: MoneyWire;
  blockedReason:
    | 'tax_information_required'
    | 'negative_balance'
    | 'below_minimum'
    | 'funds_on_hold'
    | 'payout_in_flight'
    | null;
  policy: { version: number; minimumPayout: MoneyWire; holdDays: number };
}

export interface PayoutWire {
  id: string;
  amount: MoneyWire;
  state: string;
  /** Null on payouts that predate destinations; see migration 0027. */
  method: PayoutMethodWire | null;
  destinationHint: string | null;
  policyVersion: number;
  provider: string;
  providerReference: string | null;
  processorFee: MoneyWire;
  failureReason: string | null;
  /** Sent, then handed back by the bank. Not the same as never accepted. */
  returnReason: string | null;
  requestedAt: string;
  settledAt: string | null;
  returnedAt: string | null;
}

/**
 * Why one conversion happened, for whichever party asked.
 *
 * `rationale` is deliberately `unknown`: it is the jsonb written at ingest by
 * whatever version of the resolver was running then, and typing it as today's
 * shape would be a claim the server does not make. The UI shows `summary` and
 * `explanation`, both of which are strings the server composed.
 */
export interface ExplanationWire {
  conversionId: string;
  summary: string;
  purchase: {
    merchantName: string;
    amount: MoneyWire;
    occurredAt: string;
    provider: string;
    source: string;
    eventKind: string;
  };
  commission: {
    charged: MoneyWire;
    rateCard: {
      offerName: string;
      commissionBps: number;
      wouldHaveBeen: MoneyWire;
      matchesCharged: boolean;
    } | null;
  };
  attribution: {
    rationale: unknown;
    explanation: string;
    creatorId: string;
    memberId: string;
  };
  split: {
    terms: SplitTermsWire;
    creator: MoneyWire;
    member: MoneyWire;
    platform: MoneyWire;
  };
  state: {
    current: string;
    next: string;
    reversal: { at: string; cause: string } | null;
  };
}

/**
 * The fan-side twin of `PayoutEligibilityWire`.
 *
 * Not the same type with a flag: the blocked reasons differ (a fan is never
 * asked for tax information, and is asked to accept terms instead), and
 * collapsing them would put a union member on screen that cannot occur for that
 * role. `redeemable` rather than `payable` for the same reason — the fan-facing
 * word for this is "cash out", and the API says what the UI says.
 */
export interface RedemptionEligibilityWire {
  available: MoneyWire;
  redeemable: MoneyWire;
  onHold: MoneyWire;
  blockedReason:
    | 'terms_acceptance_required'
    | 'negative_balance'
    | 'below_minimum'
    | 'funds_on_hold'
    | 'redemption_in_flight'
    | null;
  termsToAccept: { version: string; effectiveAt: string } | null;
  policy: { version: number; minimumRedemption: MoneyWire; holdDays: number };
}

export type PayoutMethodWire = 'gift_card' | 'bank_transfer';

export interface RedemptionWire {
  id: string;
  amount: MoneyWire;
  state: string;
  method: PayoutMethodWire;
  destinationHint: string;
  policyVersion: number;
  provider: string;
  providerReference: string | null;
  processorFee: MoneyWire;
  failureReason: string | null;
  /**
   * Sent, then handed back by the receiving bank — which is not the same event
   * as `failureReason`, where the provider never accepted it at all. Shown
   * separately because the remedies differ: one is "try again", the other is
   * "that account will not work, link another".
   */
  returnReason: string | null;
  requestedAt: string;
  settledAt: string | null;
  returnedAt: string | null;
}

/**
 * Where money can be sent. Never carries the provider token — the API does not
 * return it, and a destination is identified to the client by id alone.
 */
export interface PayoutDestinationWire {
  id: string;
  method: PayoutMethodWire;
  provider: string;
  displayName: string;
  verifiedBy: 'member_asserted' | 'provider_confirmed';
  createdAt: string;
}

export interface PayoutMethodInfoWire {
  method: PayoutMethodWire;
  label: string;
  timing: string;
}

/** Never the identifier itself — only what may be shown. */
export interface TaxSummaryWire {
  kind: 'ssn' | 'ein' | 'itin';
  lastFour: string;
  collectedAt: string;
}

/**
 * Which statement, and therefore which routes and which words.
 *
 * A creator statement records income; a member statement records a rebate on
 * the fan's own spending. The documents are structurally identical, which is
 * why one component renders both, and the wording is not, which is why the
 * kind has to travel with them.
 */
export type StatementKind = 'creator' | 'member';

/** The path segment each kind's statements live under. */
export function statementSurface(kind: StatementKind): 'creators' | 'members' {
  return kind === 'creator' ? 'creators' : 'members';
}

/**
 * One issued statement (migration 0028).
 *
 * The document itself is not fetched here. It is served as its own bytes, and a
 * client that parsed and re-rendered them would be showing something other than
 * the file it offers for download — so this carries the summary, and the
 * download is an ordinary link to the API.
 */
export interface StatementWire {
  id: string;
  /** `YYYY-MM`. */
  period: string;
  periodStart: string;
  periodEnd: string;
  /**
   * Null on a first statement, which covers everything up to its cut rather
   * than a month. The UI says that instead of printing a date it made up.
   */
  coveredFrom: string | null;
  generatedAt: string;
  lineCount: number;
  opening: MoneyWire;
  closing: MoneyWire;
  earned: MoneyWire;
  paidOut: MoneyWire;
  /** Covers the document exactly. Shown so a downloaded file can be checked. */
  contentSha256: string;
}

export interface TrackedLinkWire {
  id: string;
  code: string;
  merchantId: string;
  merchantName: string;
  network: string;
  terms: SplitTermsWire;
  active: boolean;
  createdAt: string;
}
